import { DateRange } from "@/services/aiDataFetchService";
import { createOffer } from "@/services/offer";
import { Variation } from "@/components/admin/locations/types/variation";
import { CampLocation } from "@/types/campLocation";
import { Firestore } from "firebase/firestore";
import { toast } from "sonner";
import { AVAILABILITY_STATUS_UNKNOWN, SIGNUP_STATUS_UNKNOWN } from "@/functions-shared/offer/offerStatusEnums";
import { sessionLinkDefaults } from "@/functions-shared/offer/sessionLinkDefaults";

export interface SessionCreationResult {
  success: boolean;
  createdCount: number;
  failedCount: number;
  errors: string[];
}

/**
 * Bulk create sessions from AI-fetched date ranges
 */
export const createSessionsFromDateRanges = async (
  db: Firestore,
  locationId: string,
  variation: Variation,
  dateRanges: DateRange[],
  userId: string
): Promise<SessionCreationResult> => {
  console.log(`[SessionFetch] Creating ${dateRanges.length} sessions for variation: ${variation.key}`);
  
  let createdCount = 0;
  let failedCount = 0;
  const errors: string[] = [];

  // Show initial loading toast
  const loadingToastId = toast.loading(`Creating ${dateRanges.length} sessions...`);

  try {
    for (const dateRange of dateRanges) {
      try {
        console.log(`[SessionFetch] Creating session for ${dateRange.startDate.toDateString()} - ${dateRange.endDate.toDateString()}`);
        
        // Prepare offer data using the program's default information
        const offerData = {
          variationKey: variation.key,
          // Use AI-extracted displayTitle if available, otherwise fall back to variation default
          displayTitle: dateRange.displayTitle || variation.displayTitle || variation.key,
          // Use AI-extracted description if available, otherwise fall back to variation default
          description: dateRange.description || variation.defaultSessionDescription || variation.description || "",
          eligibilityNotes: variation.eligibilityNotes || "",
          // Use AI-extracted URL if available, otherwise the program's default session website
          // (KB-4351: never the program page or the legacy `url`)
          url: dateRange.url || sessionLinkDefaults(variation).url,
          regUrl: sessionLinkDefaults(variation).regUrl,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          availabilityStatus: AVAILABILITY_STATUS_UNKNOWN,
          signupStatus: SIGNUP_STATUS_UNKNOWN,
          visibility: "published",
          // Set price override if AI extracted a different price
          ...(dateRange.price ? { priceOverride: parseFloat(dateRange.price.replace(/[^0-9.-]/g, '')) || 0 } : {}),
          // Set numDaysOverride if it differs from variation default
          ...(dateRange.numDays !== variation.numDays ? { numDaysOverride: dateRange.numDays } : {}),
          // Include variation data for proper calculations
          variationData: {
            key: variation.key,
            displayTitle: variation.displayTitle,
            numDays: variation.numDays || dateRange.numDays,
            regularOfferIncrementKey: variation.regularOfferIncrementKey || null,
            priceRegular: variation.priceRegular || null
          }
        };

        console.log(`[SessionFetch] Creating offer with AI-extracted data:`, {
          variationKey: offerData.variationKey,
          displayTitle: offerData.displayTitle,
          description: offerData.description?.substring(0, 50) + '...',
          url: offerData.url,
          priceOverride: (offerData as any).priceOverride || 'Not set',
          numDaysOverride: (offerData as any).numDaysOverride || 'Not set',
          startDate: offerData.startDate.toISOString(),
          endDate: offerData.endDate.toISOString(),
          availabilityStatus: offerData.availabilityStatus,
          signupStatus: offerData.signupStatus,
          usingAIExtractedTitle: !!dateRange.displayTitle,
          usingAIExtractedDescription: !!dateRange.description,
          usingAIExtractedUrl: !!dateRange.url,
          usingAIExtractedPrice: !!dateRange.price
        });

        // Create the session using the existing service
        await createOffer(db, locationId, offerData, userId);
        createdCount++;
        
        console.log(`[SessionFetch] Successfully created session ${createdCount}/${dateRanges.length}`);
        
      } catch (error) {
        console.error(`[SessionFetch] Error creating session for ${dateRange.startDate.toDateString()}:`, error);
        failedCount++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`${dateRange.startDate.toDateString()}: ${errorMessage}`);
      }
    }

    // Dismiss loading toast
    toast.dismiss(loadingToastId);

    // Show final result
    if (createdCount > 0) {
      toast.success(`Successfully created ${createdCount} session${createdCount !== 1 ? 's' : ''}`);
    }
    
    if (failedCount > 0) {
      toast.error(`Failed to create ${failedCount} session${failedCount !== 1 ? 's' : ''}`);
    }

    console.log(`[SessionFetch] Bulk creation complete: ${createdCount} created, ${failedCount} failed`);

    return {
      success: createdCount > 0,
      createdCount,
      failedCount,
      errors
    };

  } catch (error) {
    console.error(`[SessionFetch] Bulk creation failed:`, error);
    toast.dismiss(loadingToastId);
    toast.error("Failed to create sessions");
    
    return {
      success: false,
      createdCount,
      failedCount: dateRanges.length - createdCount,
      errors: [error instanceof Error ? error.message : 'Unknown error']
    };
  }
}; 